<template>
  <div class="vyopta-dashboard-container">
    <GridPanel
      v-for="(panel, index) in computedPanels"
      :key="index"
      class="panelContainer"
      :class="['width-'+panel.width, 'height-'+panel.height]"
      :panelHeader="panel.title"
    >
      <component
        :is="getComponent(panel.component)"
        @clickCallback="handleFilterClick"
        :panel="panel"
        :date-range="dateRange"
        :filter="filterModel"
      ></component>
    </GridPanel>
  </div>
</template>

<script>
import GridPanel from '../../gridPanel.vue';
// panel contents
import BarChart from './panels/BarChart.vue';
import KPI from './panels/KPI.vue';
import PieChart from './panels/PieChart.vue';
import TrendChart from './panels/TrendChart.vue';
import StackedBar from './panels/StackedBar.vue';

const PANEL_COMPONENT_MAP = {
  'PieChart': PieChart,
  'BarChart': BarChart,
  'KPI': KPI,
  'TrendChart': TrendChart,
  'StackedBar': StackedBar,
}

export default {
  name: 'DashboardContainer',
  components: {
    GridPanel,
      BarChart,
      KPI,
      PieChart,
      TrendChart,
    },
    props: {
      dateRange: {
        type: Object,
        default: undefined,
      },
      filterModel: {
        type: Object,
        default: undefined,
      },
      columnMetadata: {
        type: Array,
        default: () => [],
      },
      panels: { type: Array, default: () => [
          // { id: 'uuid-Pie-BuildingDistributionByRegion', title: 'Buildings By Region', aggregateFact: 'summaryview.buildingDistributionByRegion', component: 'PieChart', width: 2, height: 3, x: 0, y: 0 },
          // { id: 'uuid-Bar-BuildingDistributionByCountry', title: 'Buildings By Country', aggregateFact: 'summaryview.buildingDistributionByCountry', component: 'BarChart', width: 2, height: 3, x: 2, y: 0 },
          // { id: 'uuid-Bar-BuildingDistributionByCity', title: 'Buildings By City', aggregateFact: 'summaryview.buildingDistributionByCity', component: 'BarChart', width: 2, height: 3, x: 4, y: 0 },
          // // panels to implement:
          // { id: 'uuid-Kpi-TotalUsage', title: 'Total Usage', aggregateFact:'summaryview.buildingUsageSummary', kpiColumn: 'totalusagehpw', units: 'hrs/wk', icon: 'fal fa-calendar-range', component: 'KPI', width: 2, height: 1, x: 0, y: 1 },
          // { id: 'uuid-Kpi-AverageUsage', title: 'Avg Space Usage', aggregateFact:'summaryview.buildingUsageSummary', kpiColumn: 'avgroomusagehpw', units: 'hrs/wk', icon: 'fal fa-calendar-range', component: 'KPI', width: 2, height: 1, x: 2, y: 1 },
          // { id: 'uuid-Kpi-MaxUsage', title: 'Max Space Usage', aggregateFact:'summaryview.buildingUsageSummary', kpiColumn: 'maxroomusagehpw', units: 'hrs/wk', icon: 'fal fa-calendar-range', component: 'KPI', width: 2, height: 1, x: 4, y: 1 },
          // { id: 'uuid-Trend-RoomUsage', title: 'Avg Room Usage Over Time', aggregateFact:'summaryview.buildingRoomUsageTrend', component: 'TrendChart', groupByColumn: 'dimbuilding.buildingname', nameColumn: 'dimdate.weekstartdate', valueColumn: 'totalusagehour', yAxisTitle: 'Hours', width: 6, height: 4, x: 0, y: 2 },
          // { id: 'uuid-Stacked-Bar-RoomCount', title: 'Room Count By Usage Class', aggregateFact:'summaryview.buildingRoomUsageDistributionByUsageClass', component: 'StackedBar', groupByColumn: 'usageclass', nameColumn: 'dimbuilding.buildingname', valueColumn: 'roomcount', yAxisTitle: 'Room Count', width: 6, height: 4, x: 0, y: 3 }, // todo make half width when other panel is added
          // { id: 'uuid-Stacked-Bar-BuildingUsageByActivityType', title: 'Avg Usage Hours By Usage Type', aggregateFact:'summaryview.buildingUsageByUsageType', component: 'StackedBar', groupByColumn: 'usageclass', nameColumn: 'dimbuilding.buildingname', valueColumn: 'avgroomusagehpw', yAxisTitle: 'Room Count', width: 3, height: 4, x: 3, y: 3 },
        ]
      },
    },
    data: function() {
      return {
        computedPanels: computePanels(this.panels, this.filterModel),
      }
    },
    methods: {
      getComponent: function(componentName) {
        return PANEL_COMPONENT_MAP[componentName];
      },
      // todo: not all clicks will filter
      handleFilterClick: function(field, value) {
        let activeFilters = this.filterModel.getActiveFilters();
        let column = this.columnMetadata.find(col => col.id === field);
        let filter = {
          field: field,
          fieldText: column.name,
          filterComplete: true,
          operatorText: 'in',
          operatorValue: '=in=(|)',
          queryVar: value, // actual value
          shortText: value,
          text: value,
          type: 'string',
          value: field + '=in=(\"' + value + '\")', // filter code for server
        };
        if (column.displayId || column.displayField) {
          filter.displayField = column.displayId || column.displayField;
        }
        if (column.knownValuePath) {
          filter.knownValuePath = column.knownValuePath;
        }
        if (column.treeValueBranchSelect) {
          filter.treeValueBranchSelect = column.treeValueBranchSelect;
        }
        if (column.filterWidth) {
          filter.filterWidth = column.filterWidth;
        }
        if (column.knownValues) {
          filter.knownValues = column.knownValues;
        }
        if (column.datasetId) {
          filter.datasetId = column.datasetId;
        }
        let filters = activeFilters.filterGroups[0].filters;
        // if only one filter and no field, it's the default empty filter
        if (filters.length === 1 && filters[0].field === undefined) {
          filters = [];
          filters.push(filter);
        } else if (filters.length > 0) {
          let existingFilter = filters.find(f => f.field === field);
          if (existingFilter) {
            let existingValue = existingFilter.queryVar;
            if (existingValue.indexOf(value) === -1) {
              // if the value is not already in the filter, add it
              let newValue = existingValue + ',' + value;
              existingFilter.queryVar = newValue;
              existingFilter.shortText = newValue;
              existingFilter.value = field + '=in=(\"' + newValue + '\"")';
            }
          } else {
            filters.push(filter);
          }
        } else {
          filters.push(filter);
        }
        activeFilters.filterGroups[0].filters = filters;
        this.$emit('filters-updated', activeFilters.filterGroups);
      },
    },
    watch: {
      panels: {
        handler: function() {
          this.computedPanels = computePanels(this.panels, this.filterModel);
        },
        deep: true,
      },
    },
  };

  function computePanels(panels, filterModel) {
    return panels;
  }
</script>

<style lang="scss" scoped>
.vyopta-dashboard-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding-bottom: 20px;
  .panelContainer {
    flex-grow: 0;
    flex-shrink: 0;
  }
  .width-1 {
    width: calc(16.6% - 17px);
  }
  .width-2 {
    width: calc(33.3% - 14px);
  }
  .width-3 {
    width: calc(50% - 10px);
  }
  .width-4 {
    width: calc(66.6% - 7px);
  }
  .width-5 {
    width: calc(83.3% - 3px);
  }
  .width-6 {
    width: 100%;
  }
  .height-1 {
    height: 120px;
  }
  .height-2 {
    height: 240px;
  }
  .height-3 {
    height: 360px;
  }
  .height-4 {
    height: 480px;
  }
  .height-5 {
    height: 600px;
  }
  .height-6 {
    height: 720px;
  }
  @media (max-width: 1279px) {
    .width-1, .width-2, .width-3 {
      width: calc(50% - 10px);
    }
    .width-4, .width-5, .width-6 {
      width: 100%;
    }
  }
  @media (max-width: 959px) {
    .width-1 {
      width: calc(50% - 10px);
    }
    .width-2, .width-3, .width-4, .width-5, .width-6 {
      width: 100%;
    }
  }
  @media (max-width: 374px) {
    .width-1, .width-2, .width-3, .width-4, .width-5, .width-6 {
      width: 100%;
    }
  }
}
</style>
